import { ObjectId } from "bson";
import { format, subHours } from "date-fns";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import StandardButton from "../../../components/Buttons/StandardButton";
import HeaderBar from "../../../components/Header/HeaderBar";
import InputWrapper from "../../../components/Inputs/InputWrapper";
import LoadingIcon from "../../../components/Loaders/LoadingIcon";
import axios from "../../../services/axios/backendAxios";
import errors from "../../../services/errors";
import flash from "../../../services/flash";
import EmployeeSelect from "../../../components/Filters/EmployeeSelect";
import { useQueryClient } from "@tanstack/react-query";
import { CurrentUser } from "../../../store/storeTypes";
import { TimeRecord } from "../../../types";
import ModalWrapper from "../../../components/Modals/ModalWrapper";
import { SimpleLatLng } from "../../../components/SiteMap/Core";
import { AdvancedMarker, APIProvider, Map } from "@vis.gl/react-google-maps";
import { API_KEY } from "../../../config/maps";
import SubtleButton from "../../../components/Buttons/SubtleButton";

const TimeRecordPage = (props: { currentUser: CurrentUser }) => {
  const { record_id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<Partial<TimeRecord> | null>(null);
  const [isNew] = useState(record_id === "new");
  const queryClient = useQueryClient();
  const [mapPin, setMapPin] = useState<SimpleLatLng | null>(null);

  const loadData = async (signal?: AbortSignal) => {
    try {
      if (record_id === "new") {
        setRecord({
          _id: new ObjectId().toString(),
          companyId: props.currentUser.currentCompanyId!,
          startTime: subHours(new Date(), 1),
          endTime: null,
          userId: searchParams.get("user_id") ?? undefined,
        });
      } else {
        const recordData = (
          await axios.get(`timeclock/record/${record_id}`, {
            signal: signal,
          })
        ).data;
        setRecord(recordData);
      }
      setLoading(false);
    } catch (err) {
      // @ts-expect-error don't know error's type
      if (err.code !== "ERR_CANCELED") {
        errors.report(err);
        alert(
          "Sorry we were not able to load the data.  Please try refreshing.",
        );
      }
    }
  };

  useEffect(() => {
    // Note React intentionally calls this twice in dev mode so we need a way to abort: https://react.dev/learn/synchronizing-with-effects#how-to-handle-the-effect-firing-twice-in-development
    const controller = new AbortController();
    loadData(controller.signal);
    return () => {
      controller.abort();
    };
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setField = (field: keyof TimeRecord, value: any) => {
    const newObj = { ...record };
    newObj[field] = value;
    setRecord(newObj);
  };
  const save = async () => {
    try {
      setLoading(true);
      const resp = await axios.post(`timeclock/record`, record);
      setRecord(resp.data);
      setLoading(false);
      flash.success("Saved record");
      queryClient.invalidateQueries({
        queryKey: ["time-records"],
      });
      // navigate("/admin/timeclock");
      navigate(-1);
    } catch (err) {
      errors.report(err, {
        message: "Unable to save edits to a record to the backend",
      });
      // @ts-expect-error don't know the error type
      alert("Error trying to save: " + err?.response?.data);
      loadData();
    }
  };
  const deleteRecord = async () => {
    try {
      setLoading(true);
      await axios.delete(`timeclock/record/${record_id}`);
      flash.success("Record deleted");
      navigate("/admin/timeclock");
    } catch (err) {
      errors.report(err, { message: "Unable to delete a time record" });
      // @ts-expect-error don't know the error type
      alert("Error trying to delete: " + err?.response?.data);
      loadData();
    }
  };
  const canSave = () => {
    return record?.startTime != null && record?.userId != null;
  };
  return (
    <>
      <div className="flex flex-col items-center">
        <HeaderBar
          buttons={
            isNew
              ? []
              : [
                  {
                    color: "red",
                    label: "Delete Record",
                    onClick: () => deleteRecord(),
                  },
                ]
          }
          title="Time Record"
        />
        {loading ? (
          <LoadingIcon />
        ) : (
          <div className="w-full px-10 pb-10">
            <InputWrapper label="Employee">
              <EmployeeSelect
                currentCompanyId={props.currentUser.currentCompanyId}
                selectedEmployeeId={record?.userId}
                setSelectedEmployeeId={(id: string) => setField("userId", id)}
                isDisabled={!isNew}
              />
            </InputWrapper>

            <InputWrapper
              label="Start Time"
              description="Select the start time for this time record"
            >
              <input
                className="w-full text-lg font-regular border-none bg-transparent"
                onChange={(e) =>
                  setField("startTime", new Date(e.target.value).toISOString())
                }
                type="datetime-local"
                value={
                  record?.startTime
                    ? format(new Date(record.startTime), "yyyy-MM-dd'T'HH:mm")
                    : undefined
                }
                max={
                  record?.endTime
                    ? format(new Date(record.endTime), "yyyy-MM-dd'T'HH:mm")
                    : undefined
                }
              />
              {record?.crewLocationStart?.lat &&
                record?.crewLocationStart.lng && (
                  <a
                    className="underline cursor-pointer"
                    onClick={() => {
                      setMapPin(record.crewLocationStart!);
                    }}
                  >
                    Click to view location
                  </a>
                )}
            </InputWrapper>
            <InputWrapper
              label="End Time"
              description="Select the end time for this time record"
            >
              <label>
                <input
                  type="checkbox"
                  checked={record?.endTime === null}
                  onChange={(e) => {
                    setField("endTime", e.target.checked ? null : new Date());
                  }}
                />{" "}
                Active Record
              </label>

              {record?.endTime ? (
                <input
                  className="w-full text-lg font-regular border-none bg-transparent"
                  onChange={(e) =>
                    setField("endTime", new Date(e.target.value).toISOString())
                  }
                  type="datetime-local"
                  value={
                    record.endTime
                      ? format(new Date(record.endTime), "yyyy-MM-dd'T'HH:mm")
                      : undefined
                  }
                  min={
                    record.endTime
                      ? format(
                          new Date(record.startTime!),
                          "yyyy-MM-dd'T'HH:mm",
                        )
                      : undefined
                  }
                  max={format(new Date(), "yyyy-MM-dd'T'HH:mm")}
                />
              ) : (
                ""
              )}
              {record?.crewLocationEnd?.lat && record?.crewLocationEnd.lng && (
                <a
                  className="underline cursor-pointer"
                  onClick={() => {
                    setMapPin(record.crewLocationEnd!);
                  }}
                >
                  Click to view location
                </a>
              )}
            </InputWrapper>
            {isNew ? null : (
              <>
                <InputWrapper
                  label="Record Created By"
                  description="The user who was logged into the app when this record was created"
                >
                  <p>
                    {record?.submittedBy?.firstName}{" "}
                    {record?.submittedBy?.lastName} at{" "}
                    {record?.createdAt &&
                      format(new Date(record.createdAt), "yyyy-MM-dd HH:mm")}
                  </p>
                </InputWrapper>
                <InputWrapper label="Last Modified Date">
                  <p>
                    {record?.updatedAt &&
                      format(new Date(record.updatedAt), "yyyy-MM-dd HH:mm")}
                  </p>
                </InputWrapper>

                {record?.wasEdited ? (
                  <div className="py-4">
                    <span style={{ marginRight: 7 }}>&#9432;</span>Notice: This
                    record has had it's start or end time modified by an admin.
                  </div>
                ) : null}
              </>
            )}

            <div className="text-sm text-snow-primary font-light mb-1">
              All dates on this page are displayed in your local timezone (
              {Intl.DateTimeFormat().resolvedOptions().timeZone}).
              <br />
              All reports will use the company payroll timezone.
            </div>
            <div style={{ maxWidth: 600, marginTop: 20 }}>
              <StandardButton
                label={isNew ? "Create" : "Save"}
                onClick={save}
                disabled={!canSave()}
              />
            </div>
          </div>
        )}
      </div>
      {mapPin && <CrewLocationMap close={() => setMapPin(null)} pin={mapPin} />}
    </>
  );
};

const mapStateToProps = (state: { currentUser: CurrentUser }) => {
  const { currentUser } = state;
  return { currentUser };
};

export default connect(mapStateToProps)(TimeRecordPage);

function CrewLocationMap({
  close,
  pin,
}: {
  close: () => void;
  pin: SimpleLatLng;
}) {
  return (
    <ModalWrapper clickOff={close}>
      <div
        className={`flex flex-col  rounded-xl bg-snow-white drop-shadow-2xl px-12 pt-6 pb-6 overflow-x-scroll`}
      >
        <APIProvider apiKey={API_KEY}>
          <Map
            id="property-site-map"
            mapId={"property-site-map"}
            mapTypeId="satellite"
            style={{
              width: "600px",
              height: "600px",
            }}
            colorScheme="FOLLOW_SYSTEM"
            defaultCenter={pin}
            defaultTilt={0}
            defaultZoom={17}
            gestureHandling={"greedy"}
            reuseMaps={true}
            controlSize={34}
            disableDefaultUI={true}
            fullscreenControl={false}
          >
            <AdvancedMarker key={"advanced-marker"} position={pin} />
          </Map>
          <div className="mt-2 w-[600px]">
            <p>
              Remember, locations are approximate and are only available if the
              crew has GPS and network connection.
            </p>
            <SubtleButton onClick={close} buttonRole="primary">
              Done
            </SubtleButton>
          </div>
        </APIProvider>
      </div>
    </ModalWrapper>
  );
}
